import { type ReactNode } from 'react'
import { toast } from 'sonner'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '#app/components/ui/alert.tsx'
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from '#app/components/ui/avatar.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { Checkbox } from '#app/components/ui/checkbox.tsx'
import { Combobox } from '#app/components/ui/combobox.tsx'
import { type Command } from '#app/components/ui/command.matcher.ts'
import { CommandPalette } from '#app/components/ui/command.tsx'
import { DatePicker, TimeField } from '#app/components/ui/date-picker.tsx'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogOverlay,
	DialogTitle,
} from '#app/components/ui/dialog.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { EmptyState } from '#app/components/ui/empty-state.tsx'
import { Field } from '#app/components/ui/field.tsx'
import { FormCard } from '#app/components/ui/form-card.tsx'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from '#app/components/ui/input-otp.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { PageHeader } from '#app/components/ui/page-header.tsx'
import { Pagination } from '#app/components/ui/pagination.tsx'
import {
	Popover,
	PopoverClose,
	PopoverContent,
	PopoverFooter,
	PopoverHeader,
	PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import { Progress } from '#app/components/ui/progress.tsx'
import { RadioGroup, RadioGroupItem } from '#app/components/ui/radio-group.tsx'
import { Segmented } from '#app/components/ui/segmented.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import { Separator } from '#app/components/ui/separator.tsx'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetOverlay,
	SheetTitle,
} from '#app/components/ui/sheet.tsx'
import { Sidebar, type SidebarGroup } from '#app/components/ui/sidebar.tsx'
import { Skeleton } from '#app/components/ui/skeleton.tsx'
import { Slider } from '#app/components/ui/slider.tsx'
import { Spinner } from '#app/components/ui/spinner.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Switch } from '#app/components/ui/switch.tsx'
import { Table } from '#app/components/ui/table.tsx'
import { TagInput } from '#app/components/ui/tag-input.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { ToggleChip } from '#app/components/ui/toggle-chip.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'

/**
 * A styleguide specimen: one card rendered by the `/styleguide` route and
 * snapshotted into the published Claude Design bundle (see
 * `scripts/snapshot-styleguide.ts`). This module is the single source of
 * truth — both the live route and the snapshot consume it, so the published
 * design system can never drift from the real components.
 *
 * `render()` must use the real `#app/components/ui/*` components and only
 * design tokens (never hardcoded colors/fonts/radii) so the specimen stays
 * faithful to what ships.
 */
export type Specimen = {
	/** kebab-case id; becomes the snapshot filename and the Design card name. */
	name: string
	/** Section label in the Design System pane (e.g. "Colors", "Forms"). */
	group: string
	/** Short note on what variants are shown. */
	subtitle?: string
	/** Card dimensions in the Design System pane. */
	viewport: { width: number; height?: number }
	render: () => ReactNode
}

// --- Foundations: color tokens ------------------------------------------------

/**
 * The token catalogs below (`semanticColors`, `typeScale`, `radii`) are the
 * exported source of truth for the foundation specimens. A drift test
 * (`specimens.test.ts`) asserts they stay in sync with the tokens defined in
 * `app/styles/tailwind.css`, so adding/removing a token there fails CI until
 * the styleguide reflects it (or it is explicitly listed as not-shown).
 */
export const semanticColors: Array<{ bg: string; fg?: string; label: string }> =
	[
		{ bg: 'background', fg: 'foreground', label: 'background' },
		{ bg: 'card', fg: 'card-foreground', label: 'card' },
		{ bg: 'popover', fg: 'popover-foreground', label: 'popover' },
		{ bg: 'primary', fg: 'primary-foreground', label: 'primary' },
		{ bg: 'secondary', fg: 'secondary-foreground', label: 'secondary' },
		{ bg: 'muted', fg: 'muted-foreground', label: 'muted' },
		{ bg: 'accent', fg: 'accent-foreground', label: 'accent' },
		{ bg: 'destructive', fg: 'destructive-foreground', label: 'destructive' },
		{ bg: 'warning', label: 'warning' },
		{ bg: 'warning-soft', fg: 'warning-fg', label: 'warning-soft' },
		{ bg: 'warning-border', label: 'warning-border' },
		{ bg: 'border', label: 'border' },
		{ bg: 'border-strong', label: 'border-strong' },
		{ bg: 'input', label: 'input' },
		{ bg: 'ring', label: 'ring' },
	]

// Static class names so Tailwind's compiler sees every utility used here.
const swatchBg: Record<string, string> = {
	background: 'bg-background',
	card: 'bg-card',
	popover: 'bg-popover',
	primary: 'bg-primary',
	secondary: 'bg-secondary',
	muted: 'bg-muted',
	accent: 'bg-accent',
	destructive: 'bg-destructive',
	warning: 'bg-warning',
	'warning-soft': 'bg-warning-soft',
	'warning-border': 'bg-warning-border',
	border: 'bg-border',
	'border-strong': 'bg-border-strong',
	input: 'bg-input',
	ring: 'bg-ring',
}
const swatchFg: Record<string, string> = {
	foreground: 'text-foreground',
	'card-foreground': 'text-card-foreground',
	'popover-foreground': 'text-popover-foreground',
	'primary-foreground': 'text-primary-foreground',
	'secondary-foreground': 'text-secondary-foreground',
	'muted-foreground': 'text-muted-foreground',
	'accent-foreground': 'text-accent-foreground',
	'destructive-foreground': 'text-destructive-foreground',
	'warning-fg': 'text-warning-fg',
}

// --- Foundations: typography scale -------------------------------------------

export const typeScale: Array<{ cls: string; label: string }> = [
	{ cls: 'text-mega', label: 'mega' },
	{ cls: 'text-h1', label: 'h1' },
	{ cls: 'text-h2', label: 'h2' },
	{ cls: 'text-h3', label: 'h3' },
	{ cls: 'text-h4', label: 'h4' },
	{ cls: 'text-h5', label: 'h5' },
	{ cls: 'text-h6', label: 'h6' },
	{ cls: 'text-body-2xl', label: 'body-2xl' },
	{ cls: 'text-body-xl', label: 'body-xl' },
	{ cls: 'text-body-lg', label: 'body-lg' },
	{ cls: 'text-body-md', label: 'body-md' },
	{ cls: 'text-body-sm', label: 'body-sm' },
	{ cls: 'text-body-xs', label: 'body-xs' },
	{ cls: 'text-body-2xs', label: 'body-2xs' },
	{ cls: 'text-caption', label: 'caption' },
	{ cls: 'text-button', label: 'button' },
]

// --- Foundations: radii -------------------------------------------------------

export const radii: Array<{ cls: string; label: string }> = [
	{ cls: 'rounded-sm', label: 'radius-sm' },
	{ cls: 'rounded-md', label: 'radius-md' },
	{ cls: 'rounded-lg', label: 'radius-lg' },
	{ cls: 'rounded-xl', label: 'radius-xl' },
]

// --- Foundations: neutral ramp + interaction aliases (ADR 081) ---------------

/**
 * The 10-step neutral ramp (private primitive beneath the public ADR-079 names)
 * and the interaction aliases it feeds. Rendered as raw `var(--…)` swatches
 * because the numbered steps are deliberately NOT exposed as `bg-*` utilities;
 * the drift test lists them in `COLORS_NOT_SHOWN` since this dedicated specimen —
 * not the semantic Colors grid — is where they live.
 */
export const rampSteps = Array.from({ length: 10 }, (_, i) => `gray-${i + 1}`)
export const rampAliases = ['bg-hover', 'bg-active', 'border-hover'] as const

// --- Foundations: typography tracking (ADR 081) ------------------------------

export const trackingScale: Array<{ token: string; label: string }> = [
	{ token: '--tracking-mega', label: 'mega' },
	{ token: '--tracking-h1', label: 'h1' },
	{ token: '--tracking-h2', label: 'h2' },
	{ token: '--tracking-h3', label: 'h3' },
	{ token: '--tracking-h4', label: 'h4' },
	{ token: '--tracking-h5', label: 'h5' },
	{ token: '--tracking-h6', label: 'h6' },
	{ token: '--tracking-body', label: 'body' },
	{ token: '--tracking-eyebrow', label: 'eyebrow' },
]

// --- Foundations: elevation tiers (ADR 081) ----------------------------------

export const elevationTiers: Array<{ token: string; label: string }> = [
	{ token: 'flat', label: 'flat (border only)' },
	{ token: 'var(--shadow-raised)', label: 'shadow-raised' },
	{ token: 'var(--shadow-overlay)', label: 'shadow-overlay' },
]

// --- Foundations: motion namespace (ADR 081) ---------------------------------

export const motionTokens: Array<{ token: string; label: string }> = [
	{ token: '--duration-fast', label: 'duration-fast (150ms)' },
	{ token: '--duration-base', label: 'duration-base (220ms)' },
	{ token: '--duration-slow', label: 'duration-slow (350ms)' },
	{ token: '--ease-out', label: 'ease-out' },
	{ token: '--ease-in-out', label: 'ease-in-out' },
]

export const motionKeyframes = [
	'fade-rise',
	'pill-enter',
	'check-pop',
	'month-swap',
	'skel-pulse',
	'route-bar-soft',
	'route-pulse-dot',
] as const

const buttonVariants = [
	'default',
	'destructive',
	'outline',
	'secondary',
	'ghost',
	'link',
] as const
const buttonTextSizes = ['sm', 'default', 'lg', 'pill'] as const
const buttonIconSizes = ['icon-sm', 'icon', 'icon-lg'] as const
const badgeVariants = [
	'default',
	'secondary',
	'destructive',
	'outline',
	'brand',
	'warning',
] as const
const alertSpecimens = [
	{
		variant: 'info',
		title: 'Heads up',
		body: 'A neutral, informational note.',
	},
	{ variant: 'success', title: 'Saved', body: 'Your changes were saved.' },
	{
		variant: 'warning',
		title: 'Careful',
		body: 'This action needs attention.',
	},
	{
		variant: 'destructive',
		title: 'Something went wrong',
		body: 'We could not save your changes.',
	},
] as const

// A representative ⌘K registry for the palette specimen: Navigation / Theme /
// Help groups, a mix of `href` and `run` commands, each with a leading icon.
const commandRegistry: Command[] = [
	{
		id: 'home',
		title: 'Home',
		group: 'Navigation',
		href: '/',
		icon: 'arrow-right',
	},
	{
		id: 'pricing',
		title: 'Pricing',
		group: 'Navigation',
		href: '/#pricing',
		icon: 'file-text',
	},
	{
		id: 'work',
		title: 'Our work',
		group: 'Navigation',
		href: '/#work',
		icon: 'link-2',
	},
	{
		id: 'toggle-theme',
		title: 'Toggle theme',
		group: 'Theme',
		keywords: ['dark', 'light', 'appearance'],
		icon: 'sun',
		run: () => {},
	},
	{
		id: 'docs',
		title: 'Documentation',
		group: 'Help',
		href: '/docs',
		icon: 'question-mark-circled',
	},
	{
		id: 'support',
		title: 'Contact support',
		group: 'Help',
		icon: 'envelope-closed',
		run: () => {},
	},
]

// Suggested actions for the palette's empty / no-match state. Injected by the
// consumer (here, the specimen) — each fires a `sonner` toast (ADR 027) to
// demonstrate that running a command can surface feedback.
const commandEmptyActions: Command[] = [
	{
		id: 'create-project',
		title: 'Create project',
		group: 'Suggestions',
		icon: 'plus',
		run: () => toast.success('Create project'),
	},
	{
		id: 'invite',
		title: 'Invite',
		group: 'Suggestions',
		icon: 'avatar',
		run: () => toast.success('Invite teammate'),
	},
	{
		id: 'toggle-theme-empty',
		title: 'Toggle theme',
		group: 'Suggestions',
		icon: 'sun',
		run: () => toast.success('Toggle theme'),
	},
	{
		id: 'contact-support',
		title: 'Contact support',
		group: 'Suggestions',
		icon: 'question-mark-circled',
		run: () => toast.success('Contact support'),
	},
]

// Section-navigation rosters for the reference screens, mirroring the real
// `accountGroups` (settings/profile/_layout.tsx) and `adminGroups`
// (admin/_layout.tsx) so the published shells match what the app actually
// renders into the AppShell sidebar slot.
const accountNavGroups: SidebarGroup[] = [
	{
		label: 'Account',
		items: [{ to: '/settings/profile', label: 'General', icon: 'avatar' }],
	},
	{
		label: 'Security',
		items: [
			{
				to: '/settings/profile/change-email',
				label: 'Email',
				icon: 'envelope-closed',
			},
			{
				to: '/settings/profile/password',
				label: 'Password',
				icon: 'dots-horizontal',
			},
			{
				to: '/settings/profile/two-factor',
				label: 'Two-Factor',
				icon: 'lock-closed',
			},
			{
				to: '/settings/profile/connections',
				label: 'Connections',
				icon: 'link-2',
			},
			{ to: '/settings/profile/passkeys', label: 'Passkeys', icon: 'passkey' },
		],
	},
]

const adminNavGroups: SidebarGroup[] = [
	{
		label: 'Manage',
		items: [
			{ to: '/admin/blog', label: 'Blog', icon: 'file-text' },
			{ to: '/admin/cache', label: 'Cache', icon: 'clock' },
		],
	},
]

/**
 * The pine logo tile (a `rounded bg-brand` square carrying the inline two-tier
 * pine glyph in `text-primary-foreground`), mirroring `_auth/_layout.tsx`'s
 * `AuthBrand`. The sprite has no pine icon, so the glyph is inline SVG drawn
 * with `currentColor` so it tracks the tile's text colour in light and dark.
 * Sized by the caller (the auth lockup is a large `size-12 rounded-xl`; the
 * navbar uses a small `size-8 rounded-lg`).
 */
function PineMark({
	className = 'size-12 rounded-xl',
	glyphClassName = 'size-7',
}: {
	className?: string
	glyphClassName?: string
}) {
	return (
		<div
			className={`bg-brand text-primary-foreground flex items-center justify-center shadow-sm ${className}`}
		>
			<svg
				viewBox="0 0 24 24"
				className={glyphClassName}
				aria-hidden
				focusable="false"
			>
				<polygon points="12,3 7,11 17,11" fill="currentColor" />
				<polygon points="12,8 6,16 18,16" fill="currentColor" />
				<rect x="11" y="15" width="2" height="5" rx="0.5" fill="currentColor" />
			</svg>
		</div>
	)
}

/**
 * The "Open Sourced" brand lockup (▲ on a `bg-brand` tile beside the stacked
 * open/sourced wordmark) — the single navbar/footer mark (ADR-068, EPT-81),
 * mirroring `logo.tsx`. The navbar logo; the auth centered hero keeps the
 * separate {@link PineMark} tile (matching `_auth/_layout.tsx`'s `AuthBrand`).
 */
function BrandLockup() {
	return (
		<span className="flex items-center gap-2.5">
			<span
				aria-hidden
				className="bg-brand text-primary-foreground text-body-xs grid size-8 shrink-0 place-items-center rounded-lg font-bold"
			>
				▲
			</span>
			<span className="grid leading-snug">
				<span className="text-foreground text-body-xs font-bold tracking-tight">
					open
				</span>
				<span className="text-muted-foreground text-[0.625rem] font-semibold tracking-[0.2em] uppercase">
					sourced
				</span>
			</span>
		</span>
	)
}

/**
 * The universal top navbar from `app-shell.tsx` (post EPT-81/82), rebuilt as a
 * self-contained, token-styled strip for the reference-screen compositions (the
 * real `AppShell` pulls request-info-backed switches that can't render outside a
 * route). `minimal` is the borderless auth pass-through (brand lockup + theme
 * toggle); `full` adds the „Zurück zur Website" back-link and a right cluster of
 * the accent-customizer pill + the avatar identity (ringed avatar + name).
 */
function ShellNavbar({ variant }: { variant: 'minimal' | 'full' }) {
	return (
		<header
			className={`bg-background/80 border-border backdrop-blur ${variant === 'full' ? 'border-b' : ''}`}
		>
			<nav
				aria-label="Primary"
				className="flex h-15 items-center justify-between gap-6 px-6"
			>
				<div className="flex items-center gap-6">
					<BrandLockup />
					{variant === 'full' ? (
						<span className="text-muted-foreground text-body-xs">
							Zurück zur Website
						</span>
					) : null}
				</div>
				<div className="flex items-center gap-2.5">
					{variant === 'full' ? (
						/* Accent-customizer trigger pill (collapsed): the active accent
						   swatch + name. Subsumes the bare theme toggle on full/marketing
						   surfaces (ADR-062). */
						<span className="bg-card text-card-foreground border-border flex items-center gap-2 rounded-full border py-1.5 pr-3 pl-1.5">
							<span className="bg-brand size-5 rounded-full" />
							<span className="text-body-xs font-medium">Teal</span>
						</span>
					) : (
						/* Minimal (auth): a ghost theme toggle, the sun glyph inline (Icon
						   isn't in the curated bundle). */
						<Button variant="ghost" size="icon-sm" aria-label="Toggle theme">
							<svg
								viewBox="0 0 24 24"
								fill="none"
								className="size-4"
								aria-hidden
							>
								<circle
									cx="12"
									cy="12"
									r="4"
									stroke="currentColor"
									strokeWidth="2"
								/>
								<path
									d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
								/>
							</svg>
						</Button>
					)}
					{variant === 'full' ? (
						<span className="flex items-center gap-2">
							<Avatar className="ring-border size-8 ring-1">
								<AvatarFallback className="bg-brand-soft text-brand">
									AL
								</AvatarFallback>
							</Avatar>
							<span className="text-body-sm font-bold">Ada Lovelace</span>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								className="text-muted-foreground size-3.5"
								aria-hidden
							>
								<path
									d="m6 9 6 6 6-6"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</span>
					) : null}
				</div>
			</nav>
		</header>
	)
}

/**
 * A representative, hand-tokenised code snippet for the code specimens. It is a
 * palette demonstration, not live pipeline output: each span carries the same
 * inline `style="color:var(--code-*)"` shape the Markdown pipeline emits
 * (`app/utils/markdown.server.ts`) onto the always-dark `--code-*` tokens, so the
 * styleguide shows the real code colours — but the tokenisation here is authored
 * by hand, not produced by Shiki.
 */
function HighlightedSnippet() {
	const tok = (color: string, italic?: boolean) => (text: string) => (
		<span
			style={{
				color: `var(${color})`,
				fontStyle: italic ? 'italic' : undefined,
			}}
		>
			{text}
		</span>
	)
	const kw = tok('--code-kw')
	const fn = tok('--code-fn')
	const str = tok('--code-string')
	const num = tok('--code-number')
	const com = tok('--code-comment', true)
	return (
		<code className="font-mono">
			<span className="block">{com('// render a Post body to safe HTML')}</span>
			<span className="block">
				{kw('export async function')} {fn('renderPostBody')}(markdown) {'{'}
			</span>
			<span className="block">
				{'  '}
				{kw('const')} html = {kw('await')} {fn('unified')}()
			</span>
			<span className="block">
				{'    '}.{fn('use')}(remarkParse).{fn('use')}(remarkGfm).{fn('process')}
				(markdown)
			</span>
			<span className="block">
				{'  '}
				{kw('return')} {fn('String')}(html) {com('// ')}
				{num('200')}
			</span>
			<span className="block">{'}'}</span>
			<span className="block"> </span>
			<span className="block">
				{str("'highlighted, sanitised, cached on read'")}
			</span>
		</code>
	)
}

export const specimens: Specimen[] = [
	{
		name: 'colors',
		group: 'Colors',
		subtitle: 'Semantic tokens (light + dark)',
		viewport: { width: 720 },
		render: () => (
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
				{semanticColors.map(({ bg, fg, label }) => (
					<div
						key={label}
						className={`border-border flex h-20 flex-col justify-between rounded-md border p-3 ${swatchBg[bg]} ${fg ? swatchFg[fg] : 'text-foreground'}`}
					>
						<span className="text-body-xs font-medium">{label}</span>
						<span className="text-body-2xs opacity-70">--{bg}</span>
					</div>
				))}
			</div>
		),
	},
	{
		name: 'brand-accent',
		group: 'Colors',
		subtitle:
			'Accent + brand tokens (Pine default) — drives --primary / --ring',
		viewport: { width: 720 },
		render: () => (
			<div className="flex flex-col gap-4">
				{/* The accent itself, carrying the fixed near-white --primary-foreground
				    it pairs with everywhere (ADR 062). */}
				<div className="bg-brand text-primary-foreground flex h-20 flex-col justify-between rounded-md p-3">
					<span className="text-body-xs font-medium">accent</span>
					<span className="text-body-2xs opacity-80">
						--brand → --primary / --ring
					</span>
				</div>
				<div className="grid grid-cols-3 gap-3">
					{[
						{ cls: 'bg-brand', label: 'bg-brand' },
						{ cls: 'bg-brand-soft', label: 'bg-brand-soft' },
						{ cls: 'bg-brand-glow', label: 'bg-brand-glow' },
					].map(({ cls, label }) => (
						<div key={label} className="border-border rounded-md border p-3">
							<div className={`mb-2 h-10 rounded-md ${cls}`} />
							<span className="text-body-2xs font-mono">{label}</span>
						</div>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-4">
					<span className="text-brand text-body-sm font-medium">
						text-brand
					</span>
					<span className="border-brand text-body-sm rounded-md border px-3 py-1">
						border-brand
					</span>
					<span className="ring-brand text-body-sm rounded-md px-3 py-1 ring-2">
						ring-brand
					</span>
				</div>
			</div>
		),
	},
	{
		name: 'typography',
		group: 'Type',
		subtitle: 'mega → caption → button',
		viewport: { width: 720 },
		render: () => (
			<div className="flex flex-col gap-4">
				{typeScale.map(({ cls, label }) => (
					<div key={label} className="flex items-baseline gap-4">
						<span className="text-muted-foreground text-body-2xs w-20 shrink-0 font-mono">
							{label}
						</span>
						<span className={cls}>The quick brown fox</span>
					</div>
				))}
			</div>
		),
	},
	{
		name: 'radii',
		group: 'Radii',
		subtitle: 'sm / md / lg / xl',
		viewport: { width: 480, height: 200 },
		render: () => (
			<div className="flex flex-wrap gap-6">
				{radii.map(({ cls, label }) => (
					<div key={label} className="flex flex-col items-center gap-2">
						<div className={`bg-muted border-border size-16 border ${cls}`} />
						<span className="text-muted-foreground text-body-2xs font-mono">
							{label}
						</span>
					</div>
				))}
			</div>
		),
	},
	{
		name: 'ramp',
		group: 'Colors',
		subtitle: '10-step neutral ramp + interaction aliases (ADR 081)',
		viewport: { width: 720 },
		render: () => (
			<div className="flex flex-col gap-4">
				<div className="grid grid-cols-5 gap-2">
					{rampSteps.map((step) => (
						<div key={step} className="flex flex-col gap-1">
							<div
								className="border-border h-14 rounded-md border"
								style={{ background: `var(--${step})` }}
							/>
							<span className="text-body-2xs text-muted-foreground font-mono">
								--{step}
							</span>
						</div>
					))}
				</div>
				<div className="grid grid-cols-3 gap-3">
					{rampAliases.map((alias) => (
						<div key={alias} className="border-border rounded-md border p-3">
							<div
								className="mb-2 h-10 rounded-md"
								style={{ background: `var(--${alias})` }}
							/>
							<span className="text-body-2xs font-mono">--{alias}</span>
						</div>
					))}
				</div>
			</div>
		),
	},
	{
		name: 'tracking',
		group: 'Type',
		subtitle: 'Display tracking scale (mega → eyebrow, ADR 081)',
		viewport: { width: 720 },
		render: () => (
			<div className="flex flex-col gap-3">
				{trackingScale.map(({ token, label }) => (
					<div key={label} className="flex items-baseline gap-4">
						<span className="text-muted-foreground text-body-2xs w-16 shrink-0 font-mono">
							{label}
						</span>
						<span
							className={
								label === 'eyebrow'
									? 'text-body-2xs text-muted-foreground uppercase'
									: 'text-body-md'
							}
							style={{ letterSpacing: `var(${token})` }}
						>
							The quick brown fox
						</span>
					</div>
				))}
			</div>
		),
	},
	{
		name: 'elevation',
		group: 'Surfaces',
		subtitle: 'flat → raised → overlay (ADR 081)',
		viewport: { width: 560, height: 240 },
		render: () => (
			<div className="flex flex-wrap gap-8 p-4">
				{elevationTiers.map(({ token, label }) => (
					<div key={label} className="flex flex-col items-center gap-3">
						<div
							className="bg-card border-border size-24 rounded-xl border"
							style={token === 'flat' ? undefined : { boxShadow: token }}
						/>
						<span className="text-muted-foreground text-body-2xs font-mono">
							{label}
						</span>
					</div>
				))}
			</div>
		),
	},
	{
		name: 'motion',
		group: 'Motion',
		subtitle: 'Duration + easing namespace and keyframes (ADR 081)',
		viewport: { width: 560 },
		render: () => (
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-1">
					{motionTokens.map(({ token, label }) => (
						<div key={token} className="flex items-center gap-3">
							<span className="text-body-2xs text-muted-foreground w-44 shrink-0 font-mono">
								{token}
							</span>
							<span className="text-body-xs">{label}</span>
						</div>
					))}
				</div>
				<div className="flex flex-wrap gap-2">
					{motionKeyframes.map((kf) => (
						<span
							key={kf}
							className="border-border text-body-2xs rounded-md border px-2 py-1 font-mono"
						>
							{kf}
						</span>
					))}
				</div>
			</div>
		),
	},
	{
		name: 'button',
		group: 'Actions',
		subtitle: '6 variants',
		viewport: { width: 560 },
		render: () => (
			<div className="flex flex-wrap gap-3">
				{buttonVariants.map((variant) => (
					<Button key={variant} variant={variant}>
						{variant}
					</Button>
				))}
			</div>
		),
	},
	{
		name: 'button-sizes',
		group: 'Actions',
		subtitle: 'sm / default / lg / pill · icon-sm / icon / icon-lg',
		viewport: { width: 560, height: 200 },
		render: () => (
			<div className="flex flex-col gap-4">
				<div className="flex flex-wrap items-center gap-3">
					{buttonTextSizes.map((size) => (
						<Button key={size} size={size}>
							{size}
						</Button>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-3">
					{buttonIconSizes.map((size) => (
						<Button key={size} size={size} aria-label={size}>
							<span aria-hidden>+</span>
						</Button>
					))}
				</div>
			</div>
		),
	},
	{
		name: 'status-button',
		group: 'Actions',
		subtitle: 'idle / pending / success / error',
		viewport: { width: 560, height: 160 },
		render: () => (
			<div className="flex flex-wrap items-center gap-3">
				<StatusButton status="idle">idle</StatusButton>
				<StatusButton status="pending" spinDelay={{ delay: 0, minDuration: 0 }}>
					pending
				</StatusButton>
				<StatusButton status="success">success</StatusButton>
				<StatusButton status="error">error</StatusButton>
			</div>
		),
	},
	{
		name: 'badge',
		group: 'Actions',
		subtitle: 'variants + tonal brand & status dots (Published / Draft)',
		viewport: { width: 480, height: 160 },
		render: () => (
			<div className="flex flex-col gap-3">
				<div className="flex flex-wrap items-center gap-2">
					{badgeVariants.map((variant) => (
						<Badge key={variant} variant={variant}>
							{variant}
						</Badge>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Badge variant="brand" dot>
						Published
					</Badge>
					<Badge variant="secondary" dot>
						Draft
					</Badge>
				</div>
			</div>
		),
	},
	{
		name: 'alert',
		group: 'Feedback',
		subtitle: 'info / success / warning / destructive variants',
		viewport: { width: 480, height: 360 },
		render: () => (
			<div className="flex w-full max-w-md flex-col gap-3">
				{alertSpecimens.map(({ variant, title, body }) => (
					<Alert key={variant} variant={variant}>
						<AlertTitle>{title}</AlertTitle>
						<AlertDescription>{body}</AlertDescription>
					</Alert>
				))}
			</div>
		),
	},
	{
		name: 'spinner',
		group: 'Actions',
		subtitle:
			'sm / md / lg — backs StatusButton, Button.pending & async states',
		viewport: { width: 480, height: 140 },
		render: () => (
			<div className="flex items-center gap-6">
				<Spinner size="sm" />
				<Spinner size="md" />
				<Spinner size="lg" />
				<div className="flex items-center gap-2">
					<Spinner size="sm" title="Saving" />
					<span className="text-muted-foreground text-body-sm">Saving…</span>
				</div>
			</div>
		),
	},
	{
		name: 'button-pending',
		group: 'Actions',
		subtitle:
			'Button.pending — spinner in place, width kept, interaction blocked',
		viewport: { width: 480, height: 120 },
		render: () => (
			<div className="flex flex-wrap items-center gap-3">
				<Button pending spinDelay={{ delay: 0, minDuration: 0 }}>
					Saving changes
				</Button>
				<Button
					variant="outline"
					pending
					spinDelay={{ delay: 0, minDuration: 0 }}
				>
					Submit
				</Button>
			</div>
		),
	},
	{
		name: 'progress',
		group: 'Actions',
		subtitle: 'determinate bar — 0 / 45 / 100%, sm track, labelled row',
		viewport: { width: 480, height: 220 },
		render: () => (
			<div className="flex w-80 flex-col gap-4">
				<Progress value={0} aria-label="Example progress at 0%" />
				<Progress value={45} aria-label="Example progress at 45%" />
				<Progress value={100} aria-label="Example progress at 100%" />
				<Progress value={70} size="sm" aria-label="Example progress at 70%" />
				<Progress value={45} label="Uploading" />
			</div>
		),
	},
	{
		name: 'skeleton',
		group: 'Actions',
		subtitle:
			'pulsing placeholder for loading states — respects reduced motion',
		viewport: { width: 480, height: 140 },
		render: () => (
			<div className="flex items-center gap-4">
				<Skeleton className="size-12 rounded-full" />
				<div className="flex flex-col gap-2">
					<Skeleton className="h-4 w-48" />
					<Skeleton className="h-4 w-32" />
				</div>
			</div>
		),
	},
	{
		name: 'card',
		group: 'Surfaces',
		subtitle: 'header (title + description) / content / footer',
		viewport: { width: 480, height: 280 },
		render: () => (
			<Card className="max-w-sm">
				<CardHeader>
					<CardTitle>Upgrade your plan</CardTitle>
					<CardDescription>
						Unlock unlimited projects and priority support.
					</CardDescription>
				</CardHeader>
				<CardContent className="text-body-sm text-muted-foreground">
					You're currently on the free plan. Upgrade any time — changes apply
					immediately and you can cancel whenever you like.
				</CardContent>
				<CardFooter className="justify-end gap-3">
					<Button variant="ghost">Maybe later</Button>
					<Button>Upgrade</Button>
				</CardFooter>
			</Card>
		),
	},
	{
		name: 'card-elevation',
		group: 'Surfaces',
		subtitle: 'elevation tiers — flat (default) · raised · interactive (hover)',
		viewport: { width: 720, height: 220 },
		render: () => (
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				{(['flat', 'raised', 'interactive'] as const).map((variant) => (
					<Card key={variant} variant={variant}>
						<CardHeader>
							<CardTitle className="text-body-sm capitalize">
								{variant}
							</CardTitle>
							<CardDescription className="text-body-xs">
								{variant === 'flat'
									? 'Border only — the default surface.'
									: variant === 'raised'
										? 'Static contact shadow.'
										: 'Hover me — border + shadow lift.'}
							</CardDescription>
						</CardHeader>
					</Card>
				))}
			</div>
		),
	},
	{
		name: 'avatar',
		group: 'Surfaces',
		subtitle: 'image with initials fallback — sized via utility classes',
		viewport: { width: 480, height: 160 },
		render: () => (
			<div className="flex items-center gap-6">
				{/* Image present — loads to a photo. */}
				<Avatar>
					<AvatarImage src="/img/user.png" alt="Ada Lovelace" />
					<AvatarFallback>AL</AvatarFallback>
				</Avatar>
				{/* No image source — degrades to initials, not a broken-image glyph. */}
				<Avatar>
					<AvatarFallback>EM</AvatarFallback>
				</Avatar>
				{/* Sized up via a utility class on the root. */}
				<Avatar className="size-16">
					<AvatarFallback className="text-lg">GR</AvatarFallback>
				</Avatar>
			</div>
		),
	},
	{
		name: 'separator',
		group: 'Surfaces',
		subtitle: 'plain rule / labeled ("or continue with") / vertical',
		viewport: { width: 480, height: 220 },
		render: () => (
			<div className="flex max-w-sm flex-col gap-6">
				{/* Plain rule — divides stacked settings sections. */}
				<div className="flex flex-col gap-3">
					<span className="text-body-sm text-muted-foreground">
						Account details
					</span>
					<Separator />
					<span className="text-body-sm text-muted-foreground">
						Danger zone
					</span>
				</div>
				{/* Labeled variant — sits between the auth email form and the
				    OAuth/passkey button rows. */}
				<Separator label="or continue with" />
				{/* Vertical rule — inline divider between two actions. */}
				<div className="flex h-5 items-center gap-3">
					<span className="text-body-sm text-muted-foreground">Edit</span>
					<Separator orientation="vertical" />
					<span className="text-body-sm text-muted-foreground">Delete</span>
				</div>
			</div>
		),
	},
	{
		name: 'page-header',
		group: 'Surfaces',
		subtitle: 'eyebrow + title — with and without actions',
		viewport: { width: 560, height: 220 },
		render: () => (
			<div className="flex w-full max-w-xl flex-col gap-8">
				{/* With actions — the admin/list header (outline + primary). */}
				<PageHeader
					eyebrow="Admin"
					title="Blog"
					actions={
						<>
							<Button variant="outline">Preview</Button>
							<Button>New post</Button>
						</>
					}
				/>
				{/* Title only — the settings/account hub header. */}
				<PageHeader eyebrow="Account" title="Settings" />
			</div>
		),
	},
	{
		name: 'form-card',
		group: 'Surfaces',
		subtitle:
			'plain (auth form) / with header (settings section) / destructive',
		viewport: { width: 560, height: 560 },
		render: () => (
			<div className="flex w-full max-w-md flex-col gap-8">
				{/* With header — the settings-section framing (title + description). */}
				<FormCard title="Profile" description="Update your name.">
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="form-card-name">Name</Label>
							<Input id="form-card-name" defaultValue="Ada Lovelace" />
						</div>
						<div className="flex justify-end">
							<Button>Save</Button>
						</div>
					</div>
				</FormCard>
				{/* Plain — the auth-form framing (a PageHeader sits above it in the
				    shell, so the card itself carries no header). */}
				<FormCard>
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="form-card-email">Email</Label>
							<Input
								id="form-card-email"
								type="email"
								placeholder="you@example.com"
							/>
						</div>
						<Button>Continue</Button>
					</div>
				</FormCard>
				{/* Destructive — the "delete account & data" zone. */}
				<FormCard
					variant="destructive"
					title="Delete account & data"
					description="This permanently removes your account and all of its data."
				>
					<Button variant="destructive">Delete account</Button>
				</FormCard>
			</div>
		),
	},
	{
		name: 'table',
		group: 'Surfaces',
		subtitle:
			'populated (selection + status pills + ⋯ menu + pager) / empty / loading',
		viewport: { width: 720, height: 720 },
		render: () => {
			type Post = {
				id: string
				title: string
				monogram: string
				status: 'published' | 'draft'
				updated: string
			}
			const posts: Post[] = [
				{
					id: '1',
					title: 'Shipping the Pine design system',
					monogram: 'S',
					status: 'published',
					updated: '2h ago',
				},
				{
					id: '2',
					title: 'Notes on grounded design',
					monogram: 'N',
					status: 'draft',
					updated: 'Yesterday',
				},
				{
					id: '3',
					title: 'Building on the Epic Stack',
					monogram: 'B',
					status: 'published',
					updated: '3d ago',
				},
			]
			// A static view of the selection contract (one row selected) so the
			// specimen snapshots the brand left bar + indeterminate header without
			// any interaction.
			const selection = {
				isSelected: (id: string) => id === '1',
				toggle: () => {},
				toggleAll: () => {},
				allSelected: false,
				someSelected: true,
			}
			const columns = [
				{
					key: 'title',
					header: 'Post',
					cell: (post: Post) => (
						<div className="flex items-center gap-3">
							<span className="bg-brand-soft text-brand text-body-sm flex size-8 items-center justify-center rounded-lg font-semibold">
								{post.monogram}
							</span>
							<span className="font-medium">{post.title}</span>
						</div>
					),
				},
				{
					key: 'status',
					header: 'Status',
					cell: (post: Post) =>
						post.status === 'published' ? (
							<Badge variant="brand" dot>
								Published
							</Badge>
						) : (
							<Badge variant="secondary" dot>
								Draft
							</Badge>
						),
				},
				{
					key: 'updated',
					header: 'Updated',
					cell: (post: Post) => post.updated,
					className: 'text-muted-foreground text-body-sm',
				},
			]
			return (
				<div className="flex w-full max-w-3xl flex-col gap-8">
					{/* Populated — the admin list: selection, status pills, ⋯ menu, pager. */}
					<Table
						aria-label="Posts"
						columns={columns}
						data={posts}
						getRowId={(post) => post.id}
						columnTemplate="1fr auto auto"
						selection={selection}
						getRowLabel={(post) => post.title}
						rowActions={(post) => (
							<>
								<DropdownMenuItem>Edit</DropdownMenuItem>
								<DropdownMenuItem>Copy link</DropdownMenuItem>
								<DropdownMenuItem>Delete {post.title}</DropdownMenuItem>
							</>
						)}
						getRowActionsLabel={(post) => `Actions for ${post.title}`}
						footer={
							<Pagination
								page={1}
								pageCount={4}
								getPageHref={(p) => `#page-${p}`}
							/>
						}
					/>
					{/* Empty — dashed brand-tiled state with a CTA. */}
					<Table
						aria-label="Posts (empty)"
						columns={columns}
						data={[]}
						getRowId={(post) => post.id}
						columnTemplate="1fr auto auto"
						emptyState={{
							icon: (
								<svg viewBox="0 0 24 24" className="size-6" aria-hidden="true">
									<path
										d="M4 5h16M4 12h16M4 19h10"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										fill="none"
									/>
								</svg>
							),
							title: 'No posts yet',
							description: 'Draft your first post and it will appear here.',
							action: <Button>New post</Button>,
						}}
					/>
					{/* Loading — Skeleton placeholder rows. */}
					<Table
						aria-label="Posts (loading)"
						columns={columns}
						data={[]}
						getRowId={(post) => post.id}
						columnTemplate="1fr auto auto"
						loading
						loadingRows={3}
					/>
				</div>
			)
		},
	},
	{
		name: 'empty-state',
		group: 'Surfaces',
		subtitle: 'default (ringed icon + CTA) / compact in-list row',
		viewport: { width: 480, height: 420 },
		render: () => {
			const inbox = (
				<svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
					<path
						d="M4 13h4l2 3h4l2-3h4M4 13V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v7M4 13v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"
						stroke="currentColor"
						strokeWidth="1.75"
						strokeLinecap="round"
						strokeLinejoin="round"
						fill="none"
					/>
				</svg>
			)
			return (
				<div className="flex w-full max-w-md flex-col gap-8">
					{/* Default — whole-surface zero-state (as Table renders it). */}
					<EmptyState
						icon={inbox}
						title="No passkeys yet"
						description="Add one to sign in with Face ID, Touch ID, or a security key."
						action={
							<Button variant="outline" size="sm">
								Add passkey
							</Button>
						}
					/>
					{/* Compact — in-list zero-state under a card hairline. */}
					<div className="border-border rounded-lg border p-4">
						<EmptyState
							variant="compact"
							icon={inbox}
							title="No connections"
							description="Link a provider to sign in faster."
							action={
								<Button variant="outline" size="sm">
									Connect
								</Button>
							}
						/>
					</div>
				</div>
			)
		},
	},
	{
		name: 'input',
		group: 'Forms',
		subtitle: 'default / disabled / invalid / adornment / password reveal',
		viewport: { width: 480, height: 320 },
		render: () => (
			<div className="flex max-w-sm flex-col gap-3">
				<Input placeholder="Default input" />
				<Input placeholder="Disabled" disabled />
				<Input placeholder="Invalid" aria-invalid defaultValue="bad value" />
				<Input
					placeholder="handle"
					leadingAdornment={<span className="text-body-xs">@</span>}
				/>
				<Input
					type="password"
					reveal
					defaultValue="hunter2"
					aria-label="Password"
				/>
			</div>
		),
	},
	{
		name: 'textarea',
		group: 'Forms',
		subtitle: 'default / invalid',
		viewport: { width: 480, height: 260 },
		render: () => (
			<div className="flex max-w-sm flex-col gap-3">
				<Textarea placeholder="Write something…" />
				<div className="flex flex-col gap-1">
					<Textarea
						aria-invalid
						defaultValue="too short"
						aria-label="Bio (invalid example)"
					/>
					<p className="text-error-text text-body-2xs">
						Please write at least 20 characters.
					</p>
				</div>
			</div>
		),
	},
	{
		name: 'label-field',
		group: 'Forms',
		subtitle: 'Label + Input',
		viewport: { width: 480, height: 140 },
		render: () => (
			<div className="grid max-w-sm gap-1.5">
				<Label htmlFor="sg-email">Email</Label>
				<Input id="sg-email" type="email" placeholder="you@example.com" />
			</div>
		),
	},
	{
		name: 'field',
		group: 'Forms',
		subtitle:
			'Label + control + one message slot (description ↔ error) — aria wired',
		viewport: { width: 480, height: 360 },
		render: () => (
			<div className="flex max-w-sm flex-col gap-5">
				<Field
					label="Email"
					htmlFor="sg-field-email"
					description="We'll only use it to send receipts."
				>
					<Input
						id="sg-field-email"
						type="email"
						placeholder="you@example.com"
					/>
				</Field>
				<Field label="Nickname" htmlFor="sg-field-nickname" optional>
					<Input id="sg-field-nickname" placeholder="Ada" />
				</Field>
				<Field
					label="Email"
					htmlFor="sg-field-email"
					error="That email is already taken."
				>
					<Input id="sg-field-email" defaultValue="taken@example.com" />
				</Field>
			</div>
		),
	},
	{
		name: 'checkbox',
		group: 'Forms',
		subtitle: 'unchecked / checked / disabled / invalid',
		viewport: { width: 480, height: 200 },
		render: () => (
			<div className="flex flex-col gap-3">
				<div className="flex items-center gap-2">
					<Checkbox id="sg-cb1" />
					<Label htmlFor="sg-cb1">Unchecked</Label>
				</div>
				<div className="flex items-center gap-2">
					<Checkbox id="sg-cb2" defaultChecked />
					<Label htmlFor="sg-cb2">Checked</Label>
				</div>
				<div className="flex items-center gap-2">
					<Checkbox id="sg-cb3" disabled />
					<Label htmlFor="sg-cb3">Disabled</Label>
				</div>
				<div className="flex flex-col gap-1">
					<div className="flex items-center gap-2">
						<Checkbox id="sg-cb4" aria-invalid />
						<Label htmlFor="sg-cb4">Accept the terms</Label>
					</div>
					<p className="text-error-text text-body-2xs">
						You must accept to continue.
					</p>
				</div>
			</div>
		),
	},
	{
		name: 'radio-group',
		group: 'Forms',
		subtitle:
			'plain (selected · unselected · disabled · invalid) / card variant',
		viewport: { width: 480, height: 360 },
		render: () => (
			<div className="flex w-full max-w-sm flex-col gap-6">
				<RadioGroup defaultValue="comfortable" aria-label="Density">
					<div className="flex items-center gap-2">
						<RadioGroupItem id="sg-rg-compact" value="compact" />
						<Label htmlFor="sg-rg-compact">Compact</Label>
					</div>
					<div className="flex items-center gap-2">
						<RadioGroupItem id="sg-rg-comfortable" value="comfortable" />
						<Label htmlFor="sg-rg-comfortable">Comfortable</Label>
					</div>
					<div className="flex items-center gap-2">
						<RadioGroupItem id="sg-rg-spacious" value="spacious" disabled />
						<Label htmlFor="sg-rg-spacious">Spacious (disabled)</Label>
					</div>
				</RadioGroup>
				<div className="flex flex-col gap-1">
					<RadioGroup aria-invalid aria-label="Plan (invalid)">
						<div className="flex items-center gap-2">
							<RadioGroupItem id="sg-rg-invalid" value="basic" />
							<Label htmlFor="sg-rg-invalid">Choose a plan</Label>
						</div>
					</RadioGroup>
					<p className="text-error-text text-body-2xs">
						Please pick an option.
					</p>
				</div>
				<RadioGroup variant="card" defaultValue="pro" aria-label="Tier">
					<RadioGroupItem value="free">
						<span className="text-body-sm font-medium">Free</span>
						<span className="text-muted-foreground text-body-xs block">
							For hobby projects.
						</span>
					</RadioGroupItem>
					<RadioGroupItem value="pro">
						<span className="text-body-sm font-medium">Pro</span>
						<span className="text-muted-foreground text-body-xs block">
							For growing teams.
						</span>
					</RadioGroupItem>
				</RadioGroup>
			</div>
		),
	},
	{
		name: 'combobox',
		group: 'Forms',
		subtitle:
			'searchable single-select — resting trigger · open panel with the chosen value checked',
		viewport: { width: 480, height: 380 },
		render: () => {
			const items = [
				{ value: 'react', label: 'React' },
				{
					value: 'solid',
					label: 'Solid',
					description: 'Fine-grained reactivity',
				},
				{ value: 'svelte', label: 'Svelte' },
				{ value: 'vue', label: 'Vue' },
			]
			return (
				<div className="flex flex-col gap-6">
					<div className="w-64">
						<Combobox
							aria-label="Framework"
							placeholder="Select a framework"
							value=""
							onValueChange={() => {}}
							items={items}
						/>
					</div>
					{/* Rendered open (defaultOpen) so the search row + listbox chrome and
					    the chosen-value check show in the card. Popover is non-modal, so
					    the always-open specimen engages no scroll-lock. */}
					<div className="relative h-64 w-64">
						<Combobox
							aria-label="Framework (open)"
							value="solid"
							onValueChange={() => {}}
							items={items}
							defaultOpen
						/>
					</div>
				</div>
			)
		},
	},
	{
		name: 'date-picker',
		group: 'Forms',
		subtitle:
			'date trigger (calendar glyph) · open calendar with today / selected states',
		viewport: { width: 480, height: 460 },
		render: () => (
			<div className="flex flex-col gap-6">
				<div className="w-64">
					<DatePicker
						aria-label="Date"
						placeholder="Select a date"
						onValueChange={() => {}}
					/>
				</div>
				{/* Rendered open (defaultOpen) so the month header, weekday row, and day
				    cells (with the selected --brand fill) show in the card. Popover is
				    non-modal, so the always-open specimen engages no scroll-lock. */}
				<div className="relative h-80 w-72">
					<DatePicker
						aria-label="Date (open)"
						value={new Date(2026, 6, 15)}
						onValueChange={() => {}}
						defaultOpen
					/>
				</div>
			</div>
		),
	},
	{
		name: 'time-field',
		group: 'Forms',
		subtitle:
			'the DatePicker sibling — Input-metric field with a leading clock glyph',
		viewport: { width: 360, height: 140 },
		render: () => (
			<div className="w-48">
				<TimeField aria-label="Time" value="09:30" onValueChange={() => {}} />
			</div>
		),
	},
	{
		name: 'segmented',
		group: 'Forms',
		subtitle:
			'raised thumb: two segments (rest · active) / four segments / disabled well',
		viewport: { width: 480, height: 240 },
		render: () => (
			<div className="flex flex-col items-start gap-5">
				<Segmented
					aria-label="View"
					value="write"
					onValueChange={() => {}}
					options={['Write', 'Preview']}
				/>
				<Segmented
					aria-label="Density"
					value="cozy"
					onValueChange={() => {}}
					options={['Compact', 'Cozy', 'Roomy', 'Wide']}
				/>
				<Segmented
					aria-label="View (disabled)"
					value="write"
					onValueChange={() => {}}
					disabled
					options={['Write', 'Preview']}
				/>
			</div>
		),
	},
	{
		name: 'switch',
		group: 'Forms',
		subtitle: 'off / on / disabled / dark-mode toggle (sun · switch · moon)',
		viewport: { width: 480, height: 240 },
		render: () => (
			<div className="flex flex-col gap-4">
				<div className="flex items-center gap-2">
					<Switch id="sg-sw-off" />
					<Label htmlFor="sg-sw-off">Off</Label>
				</div>
				<div className="flex items-center gap-2">
					<Switch id="sg-sw-on" defaultChecked />
					<Label htmlFor="sg-sw-on">On</Label>
				</div>
				<div className="flex items-center gap-2">
					<Switch id="sg-sw-disabled" disabled />
					<Label htmlFor="sg-sw-disabled">Disabled</Label>
				</div>
				{/* Dark-mode toggle composition: sun/moon glyphs flank a labelled
				    Switch. Icon isn't part of the curated bundle, so the glyphs are
				    drawn inline (mirroring the app's sun/moon sprite). */}
				<div className="flex items-center gap-3">
					<span className="text-muted-foreground" aria-hidden>
						<svg viewBox="0 0 24 24" fill="none" className="size-4">
							<circle
								cx="12"
								cy="12"
								r="4"
								stroke="currentColor"
								strokeWidth="2"
							/>
							<path
								d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
							/>
						</svg>
					</span>
					<Switch aria-label="Dark mode" defaultChecked />
					<span className="text-muted-foreground" aria-hidden>
						<svg viewBox="0 0 24 24" fill="none" className="size-4">
							<path
								d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinejoin="round"
							/>
						</svg>
					</span>
				</div>
			</div>
		),
	},
	{
		name: 'toggle-chip',
		group: 'Forms',
		subtitle: 'off / on / locked + permission-matrix grant pair (own · any)',
		viewport: { width: 480, height: 240 },
		render: () => (
			<div className="flex flex-col gap-5">
				<div className="flex items-center gap-2">
					<ToggleChip pressed={false}>off</ToggleChip>
					<ToggleChip pressed>on</ToggleChip>
					<ToggleChip
						pressed
						locked
						lockedReason="Protected by the admin floor"
					>
						locked
					</ToggleChip>
				</div>
				{/* As used per-grant in the role editor's permission matrix: an
				    own/any chip pair per row. */}
				<div className="flex flex-col gap-2">
					<span className="text-muted-foreground text-body-2xs">read</span>
					<div className="flex items-center gap-2">
						<ToggleChip pressed>own</ToggleChip>
						<ToggleChip pressed={false}>any</ToggleChip>
					</div>
				</div>
			</div>
		),
	},
	{
		name: 'input-otp',
		group: 'Forms',
		subtitle: '6-digit, two groups + separator / invalid',
		viewport: { width: 480, height: 220 },
		render: () => (
			<div className="flex flex-col gap-4">
				<InputOTP
					maxLength={6}
					value="123456"
					onChange={() => {}}
					aria-label="One-time code"
				>
					<InputOTPGroup>
						<InputOTPSlot index={0} />
						<InputOTPSlot index={1} />
						<InputOTPSlot index={2} />
					</InputOTPGroup>
					<InputOTPSeparator />
					<InputOTPGroup>
						<InputOTPSlot index={3} />
						<InputOTPSlot index={4} />
						<InputOTPSlot index={5} />
					</InputOTPGroup>
				</InputOTP>
				<div className="flex flex-col gap-1">
					<InputOTP
						maxLength={6}
						value="000"
						onChange={() => {}}
						aria-invalid
						aria-label="One-time code (invalid example)"
					>
						<InputOTPGroup>
							<InputOTPSlot index={0} />
							<InputOTPSlot index={1} />
							<InputOTPSlot index={2} />
						</InputOTPGroup>
						<InputOTPSeparator />
						<InputOTPGroup>
							<InputOTPSlot index={3} />
							<InputOTPSlot index={4} />
							<InputOTPSlot index={5} />
						</InputOTPGroup>
					</InputOTP>
					<p className="text-error-text text-body-2xs">
						That code is incorrect.
					</p>
				</div>
			</div>
		),
	},
	{
		name: 'slider',
		group: 'Forms',
		subtitle:
			'single / gradient track / range + value / Field-paired / invalid',
		viewport: { width: 480, height: 420 },
		render: () => (
			<div className="flex w-full max-w-sm flex-col gap-6">
				<Slider defaultValue={40} min={0} max={100} aria-label="Default" />
				<Slider
					defaultValue={172}
					min={0}
					max={360}
					aria-label="Hue"
					trackGradient="linear-gradient(to right, oklch(0.7 0.15 0), oklch(0.7 0.15 60), oklch(0.7 0.15 120), oklch(0.7 0.15 180), oklch(0.7 0.15 240), oklch(0.7 0.15 300), oklch(0.7 0.15 360))"
				/>
				<Slider
					defaultValue={[20, 80]}
					min={0}
					max={100}
					showValue
					aria-label="Price range"
				/>
				<Field label="Volume" htmlFor="sg-volume">
					<Slider defaultValue={60} min={0} max={100} showValue />
				</Field>
				<div className="flex flex-col gap-1">
					<Slider
						defaultValue={90}
						min={0}
						max={100}
						aria-invalid
						aria-label="Invalid"
					/>
					<p className="text-error-text text-body-2xs">
						Choose a value below 80.
					</p>
				</div>
			</div>
		),
	},
	{
		name: 'pagination',
		group: 'Navigation',
		subtitle: 'page 1 (prev disabled) / middle (ellipsis both sides) / last',
		viewport: { width: 480, height: 220 },
		render: () => (
			<div className="flex flex-col gap-6">
				<Pagination page={1} pageCount={8} getPageHref={(p) => `#page-${p}`} />
				<Pagination page={5} pageCount={10} getPageHref={(p) => `#page-${p}`} />
				<Pagination page={8} pageCount={8} getPageHref={(p) => `#page-${p}`} />
			</div>
		),
	},
	{
		name: 'tag-input',
		group: 'Forms',
		subtitle:
			'resolve-or-create multi-select — empty / with chips (focus + type to open the menu)',
		viewport: { width: 480, height: 240 },
		render: () => (
			<div className="flex w-full max-w-sm flex-col gap-6">
				{/* Empty — placeholder visible; focus + type to open the suggestion
				    menu with its "Create «query»" row. */}
				<TagInput
					aria-label="Tags (empty)"
					suggestions={['React', 'Remix', 'CSS']}
				/>
				{/* Populated — each tag is a removable chip on --secondary. */}
				<TagInput
					aria-label="Tags (with chips)"
					defaultValue={['React', 'TypeScript']}
					suggestions={['React', 'Remix', 'CSS', 'TypeScript']}
				/>
			</div>
		),
	},
	{
		name: 'select',
		group: 'Forms',
		subtitle: 'trigger matches Input — with value / Field-paired / invalid',
		// Rendered closed (click to open the real panel). Unlike Dialog / DropdownMenu,
		// Radix Select has no `modal={false}` escape — its open content is always wrapped
		// in react-remove-scroll, so an always-open specimen would lock the whole
		// styleguide page's scroll. The open panel (groups, selected check, separator) is
		// snapshotted from the isolated `.design-sync/previews/Select.tsx` instead.
		viewport: { width: 480, height: 320 },
		render: () => (
			<div className="flex w-full max-w-sm flex-col gap-6">
				<Select defaultValue="medium">
					<SelectTrigger aria-label="Size">
						<SelectValue placeholder="Select a size" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="small">Small</SelectItem>
						<SelectItem value="medium">Medium</SelectItem>
						<SelectItem value="large">Large</SelectItem>
					</SelectContent>
				</Select>
				<Field label="Plan" htmlFor="sg-plan">
					<Select>
						<SelectTrigger id="sg-plan">
							<SelectValue placeholder="Choose a plan" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="free">Free</SelectItem>
							<SelectItem value="pro">Pro</SelectItem>
						</SelectContent>
					</Select>
				</Field>
				<div className="flex flex-col gap-1">
					<Select>
						<SelectTrigger aria-invalid aria-label="Country">
							<SelectValue placeholder="Select a country" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="ch">Switzerland</SelectItem>
							<SelectItem value="de">Germany</SelectItem>
						</SelectContent>
					</Select>
					<p className="text-error-text text-body-2xs">
						This field is required.
					</p>
				</div>
			</div>
		),
	},
	{
		name: 'popover',
		group: 'Overlays',
		subtitle: 'anchored panel — header / body / footer slots',
		viewport: { width: 480, height: 340 },
		render: () => (
			// modal={false} so the always-open specimen doesn't engage a scroll-lock
			// that would freeze the styleguide page. Rendered in place (no Portal).
			<Popover open modal={false}>
				<PopoverTrigger asChild>
					<Button variant="outline">Dimensions</Button>
				</PopoverTrigger>
				<PopoverContent aria-label="Dimensions">
					<PopoverHeader>
						<p className="text-body-sm font-semibold">Dimensions</p>
						<p className="text-muted-foreground text-body-xs">
							Set the layout metrics.
						</p>
					</PopoverHeader>
					<p className="text-body-sm">Width and height controls go here.</p>
					<PopoverFooter>
						<PopoverClose asChild>
							<Button variant="ghost" size="sm">
								Cancel
							</Button>
						</PopoverClose>
						<Button size="sm">Save</Button>
					</PopoverFooter>
				</PopoverContent>
			</Popover>
		),
	},
	{
		name: 'dropdown-menu',
		group: 'Overlays',
		subtitle: 'label / items / separator',
		viewport: { width: 480, height: 280 },
		render: () => (
			// modal={false} so the always-open specimen doesn't engage Radix's
			// scroll-lock (react-remove-scroll), which would freeze mouse-wheel
			// scrolling of the whole styleguide page.
			<DropdownMenu open modal={false}>
				<DropdownMenuTrigger asChild>
					<Button variant="outline">Open menu</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent>
					<DropdownMenuLabel>My account</DropdownMenuLabel>
					<DropdownMenuSeparator />
					<DropdownMenuItem>Profile</DropdownMenuItem>
					<DropdownMenuItem>Settings</DropdownMenuItem>
					<DropdownMenuItem>Log out</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		),
	},
	{
		name: 'tooltip',
		group: 'Overlays',
		subtitle: 'open',
		viewport: { width: 480, height: 160 },
		render: () => (
			<TooltipProvider>
				<Tooltip open>
					<TooltipTrigger asChild>
						<Button variant="outline">Hover me</Button>
					</TooltipTrigger>
					<TooltipContent>Helpful hint</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		),
	},
	{
		name: 'accordion',
		group: 'Disclosure',
		subtitle: 'single-open — first item open, rest collapsed',
		viewport: { width: 560, height: 280 },
		render: () => (
			<Accordion defaultValue="item-1" className="w-full">
				<AccordionItem value="item-1">
					<AccordionTrigger>Is it accessible?</AccordionTrigger>
					<AccordionContent>
						Yes. It is built on Radix and follows the WAI-ARIA disclosure
						pattern, with full keyboard support.
					</AccordionContent>
				</AccordionItem>
				<AccordionItem value="item-2">
					<AccordionTrigger>Is it themeable?</AccordionTrigger>
					<AccordionContent>
						Yes. It is styled with design tokens only, so it follows the brand
						accent and light/dark theme automatically.
					</AccordionContent>
				</AccordionItem>
				<AccordionItem value="item-3">
					<AccordionTrigger>Does it animate?</AccordionTrigger>
					<AccordionContent>
						Yes — a grid-rows height transition, with a reduced-motion fallback.
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		),
	},
	{
		name: 'accordion-multiple',
		group: 'Disclosure',
		subtitle: 'type="multiple" — several panels open at once, one disabled',
		viewport: { width: 560, height: 320 },
		render: () => (
			<Accordion
				type="multiple"
				defaultValue={['shipping', 'returns']}
				className="w-full"
			>
				<AccordionItem value="shipping">
					<AccordionTrigger>How fast is shipping?</AccordionTrigger>
					<AccordionContent>
						Orders ship within two business days; tracking follows by email.
					</AccordionContent>
				</AccordionItem>
				<AccordionItem value="returns">
					<AccordionTrigger>What is the return window?</AccordionTrigger>
					<AccordionContent>
						Thirty days, no questions asked — both panels stay open at once
						because type="multiple" never auto-collapses siblings.
					</AccordionContent>
				</AccordionItem>
				<AccordionItem value="enterprise" disabled>
					<AccordionTrigger>Enterprise plans (coming soon)</AccordionTrigger>
					<AccordionContent>
						A disabled item renders dimmed and is skipped by keyboard focus.
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		),
	},
	{
		name: 'command',
		group: 'Overlays',
		subtitle: '⌘K palette — grouped results, brand-soft selection',
		viewport: { width: 560, height: 380 },
		// Rendered inline (no `open`) so the specimen snapshots without a portal;
		// the live overlay form takes `open`/`onOpenChange`.
		render: () => <CommandPalette commands={commandRegistry} />,
	},
	{
		name: 'command-empty',
		group: 'Overlays',
		subtitle: 'no-match state — suggested-action chips',
		viewport: { width: 560, height: 240 },
		// An empty registry forces the no-match state; the chips are injected.
		render: () => (
			<CommandPalette commands={[]} emptyActions={commandEmptyActions} />
		),
	},
	{
		name: 'command-loading',
		group: 'Overlays',
		subtitle: 'pending remote source — Skeleton/Spinner placeholders',
		viewport: { width: 560, height: 280 },
		// `loading` paints Skeleton rows + a Spinner instead of the empty/no-results
		// state, so an in-flight fetch never reads as "No results found.".
		render: () => <CommandPalette commands={[]} loading />,
	},
	{
		name: 'dialog',
		group: 'Overlays',
		subtitle: 'modal overlay — title, description, close actions',
		viewport: { width: 560, height: 320 },
		// Rendered contained: the overlay/content are positioned `absolute` within
		// this `relative` box (and modal={false}) so the static specimen shows the
		// dialog surface in place, without a full-screen portal that would lock and
		// dim the whole styleguide page.
		render: () => (
			<div className="relative flex h-72 items-center justify-center overflow-hidden rounded-lg">
				<Dialog open modal={false}>
					<DialogOverlay className="absolute" />
					<DialogContent className="absolute w-[26rem]">
						<DialogTitle>Delete project</DialogTitle>
						<DialogDescription className="mt-2">
							This permanently removes the project and all of its data. This
							action cannot be undone.
						</DialogDescription>
						<div className="mt-6 flex justify-end gap-2">
							<DialogClose asChild>
								<Button variant="outline">Cancel</Button>
							</DialogClose>
							<DialogClose asChild>
								<Button variant="destructive">Delete</Button>
							</DialogClose>
						</div>
					</DialogContent>
				</Dialog>
			</div>
		),
	},

	{
		name: 'sheet',
		group: 'Overlays',
		subtitle: 'slide-over panel — anchors to an edge (Sidebar mobile drawer)',
		viewport: { width: 560, height: 360 },
		// Rendered contained: the overlay/content are positioned `absolute` within
		// this `relative` box (and modal={false}) so the static specimen shows the
		// slide-over surface in place, without a full-screen portal that would lock
		// and dim the whole styleguide page.
		render: () => (
			<div className="relative flex h-80 items-center justify-center overflow-hidden rounded-lg">
				<Sheet open modal={false}>
					<SheetOverlay className="absolute" />
					<SheetContent side="left" className="absolute w-64">
						<SheetTitle>Account</SheetTitle>
						<SheetDescription>Jump to a settings section.</SheetDescription>
						<nav className="text-body-sm mt-4 flex flex-col gap-1">
							<a
								className="text-foreground rounded-md px-2 py-1.5"
								href="#general"
							>
								General
							</a>
							<a
								className="text-muted-foreground rounded-md px-2 py-1.5"
								href="#security"
							>
								Security
							</a>
							<a
								className="text-muted-foreground rounded-md px-2 py-1.5"
								href="#connections"
							>
								Connections
							</a>
						</nav>
						<div className="mt-auto flex justify-end">
							<SheetClose asChild>
								<Button variant="outline">Close</Button>
							</SheetClose>
						</div>
					</SheetContent>
				</Sheet>
			</div>
		),
	},

	{
		name: 'sidebar',
		group: 'Surfaces',
		subtitle: 'config-driven section rail — grouped items, active highlight',
		viewport: { width: 280, height: 360 },
		// The desktop rail (md+); below the breakpoint it collapses to a
		// hamburger that opens the same groups in a Sheet drawer. Active item
		// (and its group label) is driven by the pure resolver fed a pathname.
		render: () => (
			<div className="flex h-80 overflow-hidden rounded-lg">
				<Sidebar
					label="Account"
					pathname="/settings/profile/password"
					groups={[
						{
							label: 'Account',
							items: [
								{ to: '/settings/profile', label: 'General', icon: 'avatar' },
							],
						},
						{
							label: 'Security',
							items: [
								{
									to: '/settings/profile/password',
									label: 'Password',
									icon: 'lock-closed',
								},
								{
									to: '/settings/profile/two-factor',
									label: 'Two-Factor',
									icon: 'lock-open-1',
								},
								{
									to: '/settings/profile/connections',
									label: 'Connections',
									icon: 'link-2',
								},
							],
						},
					]}
				/>
			</div>
		),
	},

	// --- Reference screens -----------------------------------------------------
	//
	// Composition templates: whole-page layouts assembled from the curated
	// primitives only, mirroring the app's real shipping shells so Claude Design
	// grounds feature design in assembled surfaces, not just atoms. Each rebuilds
	// the AppShell chrome (`ShellNavbar`) + section `Sidebar` as a self-contained,
	// token-styled strip — the real `AppShell` pulls request-info-backed
	// theme/accent switches that can't render outside a route. They double as
	// proof that no hand-rolled primitive remains.
	{
		name: 'screen-auth',
		group: 'Reference screens',
		subtitle: 'auth shell — minimal navbar + centered FormCard (real /login)',
		viewport: { width: 480, height: 660 },
		// AppShell `minimal` (borderless navbar: logo + theme toggle) over the
		// centered single column from `_auth/_layout.tsx`: a faint brand-glow
		// radial, the pine logo lockup, an h1, then the login FormCard.
		render: () => (
			<div className="border-border bg-background w-full overflow-hidden rounded-lg border">
				<ShellNavbar variant="minimal" />
				<div className="relative flex flex-col items-center px-4 py-10">
					<div
						aria-hidden
						className="pointer-events-none absolute inset-x-0 top-0 h-40"
						style={{
							background:
								'radial-gradient(60% 100% at 50% 0%, var(--brand-glow), transparent 70%)',
						}}
					/>
					<div className="relative z-10 flex w-full flex-col items-center gap-8">
						<PineMark />
						<div className="w-full max-w-[360px]">
							<div className="flex flex-col gap-2 text-center">
								<h1 className="text-h4">Sign in to your account</h1>
								<p className="text-muted-foreground text-body-sm">
									Welcome back! Please enter your details.
								</p>
							</div>
							<FormCard className="mt-6 p-6 text-left">
								<div className="flex flex-col gap-4">
									<Field label="Email" htmlFor="screen-auth-email">
										<Input
											id="screen-auth-email"
											type="email"
											placeholder="you@example.com"
										/>
									</Field>
									<Field label="Password" htmlFor="screen-auth-password">
										<Input
											id="screen-auth-password"
											type="password"
											placeholder="••••••••"
										/>
									</Field>
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-2">
											<Checkbox id="screen-auth-remember" defaultChecked />
											<Label htmlFor="screen-auth-remember">Remember me</Label>
										</div>
										<a href="#forgot" className="text-brand text-body-sm">
											Forgot password?
										</a>
									</div>
									<StatusButton status="idle" size="wide">
										Sign in
									</StatusButton>
									<Separator label="or continue with" className="my-1" />
									<Button variant="outline" size="wide">
										Continue with GitHub
									</Button>
								</div>
							</FormCard>
							<p className="text-muted-foreground text-body-sm mt-6 text-center">
								New here?{' '}
								<a href="#signup" className="text-brand">
									Create an account
								</a>
							</p>
						</div>
					</div>
				</div>
			</div>
		),
	},
	{
		name: 'screen-section-shell',
		group: 'Reference screens',
		subtitle:
			'AppShell full — navbar + section Sidebar + content slot (account/admin frame)',
		viewport: { width: 920, height: 460 },
		// The reusable chrome every account/admin page fills: the `full` navbar
		// (identity avatar + theme toggle) over a row of the section `Sidebar`
		// rail and the content column. Settings + admin below are filled instances
		// of this same frame.
		render: () => (
			<div className="border-border bg-background flex h-[440px] w-full flex-col overflow-hidden rounded-lg border">
				<ShellNavbar variant="full" />
				<div className="flex min-h-0 flex-1">
					<Sidebar
						label="Section"
						pathname="/section/overview"
						groups={[
							{
								label: 'Section',
								items: [
									{
										to: '/section/overview',
										label: 'Overview',
										icon: 'avatar',
									},
									{ to: '/section/activity', label: 'Activity', icon: 'clock' },
								],
							},
							{
								label: 'Manage',
								items: [
									{ to: '/section/items', label: 'Items', icon: 'file-text' },
									{ to: '/section/links', label: 'Links', icon: 'link-2' },
								],
							},
						]}
					/>
					<div className="flex min-w-0 flex-1 flex-col gap-6 p-6">
						<PageHeader
							eyebrow="Section"
							title="Overview"
							headingLevel={1}
							actions={<Button>New item</Button>}
						/>
						<div className="border-border text-muted-foreground text-body-sm flex flex-1 items-center justify-center rounded-lg border border-dashed">
							Content area — each page fills this slot
						</div>
					</div>
				</div>
			</div>
		),
	},
	{
		name: 'screen-settings',
		group: 'Reference screens',
		subtitle:
			'settings page — full navbar + account Sidebar + PageHeader + FormCards (real /settings/profile)',
		viewport: { width: 920, height: 720 },
		// The account shell filled in: the `full` navbar + the account `Sidebar`
		// rail, then a PageHeader over stacked FormCards — a profile section, a
		// Switch-backed preference, and the destructive delete-account zone.
		render: () => (
			<div className="border-border bg-background flex w-full flex-col overflow-hidden rounded-lg border">
				<ShellNavbar variant="full" />
				<div className="flex min-h-0 flex-1">
					<Sidebar
						label="Account"
						pathname="/settings/profile"
						groups={accountNavGroups}
					/>
					<div className="flex min-w-0 flex-1 flex-col gap-6 p-6">
						<PageHeader eyebrow="Account" title="Settings" headingLevel={1} />
						<FormCard title="Profile" description="Update your name.">
							<div className="flex flex-col gap-4">
								<Field label="Name" htmlFor="screen-settings-name">
									<Input
										id="screen-settings-name"
										defaultValue="Ada Lovelace"
									/>
								</Field>
								<Field label="Email" htmlFor="screen-settings-email">
									<Input
										id="screen-settings-email"
										defaultValue="ada@example.com"
									/>
								</Field>
								<div className="flex justify-end">
									<StatusButton status="idle">Save</StatusButton>
								</div>
							</div>
						</FormCard>
						<FormCard
							title="Preferences"
							description="Choose what we email you about."
						>
							<div className="flex items-center justify-between gap-4">
								<div className="flex flex-col">
									<Label htmlFor="screen-settings-emails">Product emails</Label>
									<span className="text-muted-foreground text-body-2xs">
										Occasional product news and tips.
									</span>
								</div>
								<Switch id="screen-settings-emails" defaultChecked />
							</div>
						</FormCard>
						<FormCard
							variant="destructive"
							title="Delete account & data"
							description="This permanently removes your account and all of its data."
						>
							<Button variant="destructive">Delete account</Button>
						</FormCard>
					</div>
				</div>
			</div>
		),
	},
	{
		name: 'screen-admin-blog',
		group: 'Reference screens',
		subtitle:
			'admin list — full navbar + admin Sidebar + PageHeader + Table (real /admin/blog)',
		viewport: { width: 980, height: 560 },
		// The admin shell filled in: the `full` navbar + the admin `Sidebar` rail,
		// then a PageHeader with the new-post action over the populated Table
		// (row selection, status pills, ⋯ row menu, pager footer).
		render: () => {
			type Post = {
				id: string
				title: string
				monogram: string
				status: 'published' | 'draft'
				updated: string
			}
			const posts: Post[] = [
				{
					id: '1',
					title: 'Shipping the Pine design system',
					monogram: 'S',
					status: 'published',
					updated: '2h ago',
				},
				{
					id: '2',
					title: 'Notes on grounded design',
					monogram: 'N',
					status: 'draft',
					updated: 'Yesterday',
				},
				{
					id: '3',
					title: 'Building on the Epic Stack',
					monogram: 'B',
					status: 'published',
					updated: '3d ago',
				},
			]
			// A static view of the selection contract (one row selected) so the
			// screen snapshots the brand left bar + indeterminate header without
			// any interaction.
			const selection = {
				isSelected: (id: string) => id === '1',
				toggle: () => {},
				toggleAll: () => {},
				allSelected: false,
				someSelected: true,
			}
			const columns = [
				{
					key: 'title',
					header: 'Post',
					cell: (post: Post) => (
						<div className="flex items-center gap-3">
							<span className="bg-brand-soft text-brand text-body-sm flex size-8 items-center justify-center rounded-lg font-semibold">
								{post.monogram}
							</span>
							<span className="font-medium">{post.title}</span>
						</div>
					),
				},
				{
					key: 'status',
					header: 'Status',
					cell: (post: Post) =>
						post.status === 'published' ? (
							<Badge variant="brand" dot>
								Published
							</Badge>
						) : (
							<Badge variant="secondary" dot>
								Draft
							</Badge>
						),
				},
				{
					key: 'updated',
					header: 'Updated',
					cell: (post: Post) => post.updated,
					className: 'text-muted-foreground text-body-sm',
				},
			]
			return (
				<div className="border-border bg-background flex w-full flex-col overflow-hidden rounded-lg border">
					<ShellNavbar variant="full" />
					<div className="flex min-h-0 flex-1">
						<Sidebar
							label="Admin"
							pathname="/admin/blog"
							groups={adminNavGroups}
						/>
						<div className="flex min-w-0 flex-1 flex-col gap-6 p-6">
							<PageHeader
								eyebrow="Admin"
								title="Blog"
								headingLevel={1}
								actions={
									<>
										<Button variant="outline">Preview</Button>
										<Button>New post</Button>
									</>
								}
							/>
							<Table
								aria-label="Posts"
								columns={columns}
								data={posts}
								getRowId={(post) => post.id}
								columnTemplate="1fr auto auto"
								selection={selection}
								getRowLabel={(post) => post.title}
								rowActions={(post) => (
									<>
										<DropdownMenuItem>Edit</DropdownMenuItem>
										<DropdownMenuItem>Copy link</DropdownMenuItem>
										<DropdownMenuItem>Delete {post.title}</DropdownMenuItem>
									</>
								)}
								getRowActionsLabel={(post) => `Actions for ${post.title}`}
								footer={
									<Pagination
										page={1}
										pageCount={4}
										getPageHref={(p) => `#page-${p}`}
									/>
								}
							/>
						</div>
					</div>
				</div>
			)
		},
	},
	{
		name: 'code-block',
		group: 'Code',
		subtitle: 'always-dark Shiki surface, traffic-light caption (ADR 063)',
		viewport: { width: 720, height: 300 },
		// The block chrome the article + preview wrap around the pipeline's
		// highlighted `<pre>`: 0.7rem radius, 1px --code-border, a traffic-light
		// caption bar on --code-bg-2. The surface stays dark on any page theme
		// because every colour is a fixed --code-* token.
		render: () => (
			<div className="overflow-hidden rounded-[0.7rem] border border-[var(--code-border)] bg-[var(--code-bg)]">
				<div className="flex items-center gap-2 border-b border-[var(--code-border)] bg-[var(--code-bg-2)] px-4 py-2.5">
					<span className="size-3 rounded-full bg-[var(--code-comment)]" />
					<span className="size-3 rounded-full bg-[var(--code-comment)]" />
					<span className="size-3 rounded-full bg-[var(--code-comment)]" />
					<span className="text-body-2xs ml-2 font-mono text-[var(--code-comment)]">
						markdown.server.ts
					</span>
				</div>
				<pre className="text-body-xs overflow-x-auto p-4 leading-relaxed text-[var(--code-fg)]">
					<HighlightedSnippet />
				</pre>
			</div>
		),
	},
	{
		name: 'prose-article',
		group: 'Type',
		subtitle:
			'long-form reading ramp — 68ch measure, em-relative headings (ADR 064)',
		viewport: { width: 720, height: 620 },
		// The scoped `.prose` ramp rendered Markdown flows into. Exercises the
		// heading ramp, body rhythm, brand-tinted links + markers, blockquote
		// rule, the inline-code chip, and a fenced code block styled by `.prose
		// pre`. Reuses existing colour tokens (ADR 064) — no new colours.
		render: () => (
			<article className="prose">
				<h2>Render on read, cached</h2>
				<p>
					A post stores raw Markdown as the single source of truth. The loader
					renders it to safe HTML on read and caches the result, so an article
					re-renders only when its body changes.
				</p>
				<p>
					Inline spans like <code>renderPostBody()</code> sit on a soft,
					bordered chip, while links to the <a href="/blog">feed</a> pick up the
					brand accent.
				</p>
				<h3>What the pipeline guarantees</h3>
				<ul>
					<li>GFM tables, headings, and lists render predictably.</li>
					<li>Author HTML is sanitised before highlighting runs.</li>
					<li>Code blocks paint onto the always-dark code palette.</li>
				</ul>
				<blockquote>
					Raw Markdown is the source of truth; rendered HTML is never persisted.
				</blockquote>
				<pre>
					<HighlightedSnippet />
				</pre>
			</article>
		),
	},
]
